import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import {
  ColumnEditorModel,
  CriteriaSortEnum,
  FieldJunctionEnum,
  ResultTypeEnum,
  SearchCriteriaDtoModel,
  SearchDtoModel,
  SearchSortDtoModel,
  SortDtoModel
} from '@bo-client/shared/dynamic-search-serverside/dynamic-search.model';
import {
  ActionWithMoreInfoModel,
  CustomActionTypes,
  CustomFormTypes,
  ExecutionActionsTypes,
  SecondActionTypes
} from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { uri } from '@bo-client/shared/constants/uri';
import { UserDto } from '@bo-client/shared/models/userDto';
import {
  CommissioningStatusEnum,
  CommonStatusEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { AuthenticationService } from '@bo-client/core';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { CommissioningRequestDtoModel } from '@bo-client/activation-management/shared/models/commissioningRequestDto';
import { UserRole } from '@bo-client/shared/models/user-roles.enum';

@Injectable({
  providedIn: 'root'
})
export class CommissioningRequestService {
  public form = new FormGroup({});
  actions: ActionWithMoreInfoModel<CommissioningRequestDtoModel>[] = [
    {
      action: 'save',
      actionName: 'Zwischenspeichern',
      actionUrl: uri.cmCommissioningRequest,
      successMessage: 'Inbetriebnahme erfolgreich gespeichert',
      failureMessage: 'Fehler beim Speichern.',
      subject: '',
      validateForm: false,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.authenticationService.isLoggedIn &&
          (entity.state === CommissioningStatusEnum.Incomplete ||
            entity.state === CommissioningStatusEnum.DefectReported ||
            entity.state === CommissioningStatusEnum.FeedbackNeededByChimneySweep ||
            entity.state === CommissioningStatusEnum.FeedbackNeeded ||
            this.hasRole(user, ['ROLE_EDITOR_BASE']))
        );
      },
      execute: ExecutionActionsTypes.save
    },
    {
      action: 'approve',
      actionName: 'Freigeben',
      actionUrl: uri.cmApproveCommissioningRequest,
      successMessage: 'Antrag erfolgreich Freigegeben',
      failureMessage: 'Fehler bei der Freigabe des Antrages',
      subject: 'Freigabe zu Ihrem Antrag',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.authenticationService.isLoggedIn &&
          !(entity.state === CommissioningStatusEnum.Approved) &&
          this.hasRole(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'sendOffer',
      actionName: 'Angebot versenden',
      actionUrl: uri.cmCommissioningRequestSendOffer,
      successMessage: 'Angebot erfolgreich versendet',
      failureMessage: 'Fehler bei dem Versenden des Angebotes',
      recipients: 'Kunde / Installateur',
      subject: 'Angebot zu Ihrer Inbetriebnahme',
      validateForm: false,
      customForm: CustomFormTypes.CrSendOfferForm,
      execute: ExecutionActionsTypes.customFormWithCustomActionModal,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      visibleCondition: (_, state, user, tenantConfig): boolean => {
        return (
          tenantConfig?.commissioning?.activateSubjectToFeeProcess &&
          state === CommissioningStatusEnum.Requested &&
          this.hasRole(user, ['ROLE_EDITOR_COMMISSIONING'])
        );
      },
      actionType: 'primary',
      tooltip:
        'Versetzt den Vorgang in den Status <em>"Warten auf Bestellung"</em> und versendet eine E-Mail mit dem Betreff ' +
        '<em>"Kostenpflichtige Inbetriebnahme – Ihr Angebot wird vorbereitet"</em> ' +
        'an den Kunden und den verantwortlichen Installateur.'
    },
    {
      action: 'commissionOffer',
      actionName: 'Angebot beauftragt',
      actionUrl: uri.cmCommissioningRequestCommissionOffer,
      successMessage: 'Antrag erfolgreich nach "Angebot beauftragt" gesetzt',
      failureMessage: 'Fehler beim Versenden der Anfrage',
      subject: 'Ihr Antrag zur Inbetriebnahme',
      validateForm: false,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.state == CommissioningStatusEnum.WaitingForOrder &&
          this.hasRole(user, ['ROLE_EDITOR_COMMISSIONING'])
        );
      },
      execute: ExecutionActionsTypes.confirmAction
    },
    {
      action: 'technicalReview',
      actionName: 'Technische Prüfung',
      actionUrl: uri.cmCommissioningRequestTechnicalReview,
      successMessage: 'Technische Prüfung erfolgt',
      failureMessage: 'Fehler beim Setzen der Technischen Prüfung',
      recipients: 'Kunde / Installateur',
      subject: 'Technische Prüfung Ihrer Inbetriebnahme',
      validateForm: false,
      customForm: CustomFormTypes.CrSendOfferForm,
      execute: ExecutionActionsTypes.customFormWithCustomActionModal,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      visibleCondition: (_, state, user, tenantConfig): boolean => {
        return (
          tenantConfig?.commissioning?.activateTechnicalReviewProcess &&
          state === CommissioningStatusEnum.Requested &&
          this.hasRole(user, ['ROLE_EDITOR_COMMISSIONING'])
        );
      },
      actionType: 'primary',
      tooltip:
        'Versetzt den Vorgang in den Status <em>"Techn. Prüfung erfolgt"</em> und versendet eine E-Mail mit dem Betreff ' +
        '<em>"Technische Prüfung Ihrer Inbetriebnahme"</em> ' +
        'an den Kunden und den verantwortlichen Installateur.'
    },
    {
      action: 'sendNAV',
      actionName: 'NAV versenden',
      actionUrl: uri.cmCommissioningRequestSendNAV,
      successMessage: 'Antrag erfolgreich nach "NAV versendet" gesetzt',
      failureMessage: 'Fehler beim Versenden der Anfrage',
      subject: 'Ihr Antrag zur Inbetriebnahme',
      validateForm: false,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          entity.state == CommissioningStatusEnum.TechnicalReview &&
          this.hasRole(user, ['ROLE_EDITOR_COMMISSIONING'])
        );
      },
      execute: ExecutionActionsTypes.confirmAction
    },
    {
      action: 'requestFeedback',
      actionName: 'Rückfragen',
      actionUrl: uri.cmCommissioningRequestRequestFeedback,
      successMessage: 'Rückfrage versandt',
      failureMessage: 'Fehler beim Versenden der Rückfrage',
      subject: 'Rückfrage zu Ihrer Inbetriebnahme',
      validateForm: false,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.authenticationService.isLoggedIn &&
          (entity.state === CommissioningStatusEnum.Requested ||
            entity.state === CommissioningStatusEnum.Commissioned ||
            entity.state === CommissioningStatusEnum.NAVSent ||
            entity.state === CommissioningStatusEnum.Approved) &&
          this.hasRole(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'reportDefect',
      actionName: 'Mangel melden',
      actionUrl: uri.cmCommissioningRequestReportDefect,
      successMessage: 'Mängelmeldung versandt',
      failureMessage: 'Fehler beim Versenden',
      subject: 'Mängelmeldung Ihrer Inbetriebnahme',
      validateForm: false,
      customForm: CustomFormTypes.CrReportDefectForm,
      execute: ExecutionActionsTypes.customFormWithCustomActionModal,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.dataShareService.tenantConfig.commissioning.reportDefectActive &&
          this.authenticationService.isLoggedIn &&
          (entity.state === CommissioningStatusEnum.Requested ||
            entity.state === CommissioningStatusEnum.Commissioned ||
            entity.state === CommissioningStatusEnum.NAVSent ||
            entity.state === CommissioningStatusEnum.Approved) &&
          this.hasRole(user, ['ROLE_EDITOR_BASE'])
        );
      }
    },
    {
      action: 'requestApproval',
      actionName: 'Genehmigung anfragen',
      actionUrl: uri.cmCommissioningRequestRequestApproval,
      successMessage: 'Anfrage erfolgreich versandt',
      failureMessage: 'Fehler beim Versenden der Anfrage',
      subject: 'Ihre Anfrage zur Inbetriebnahme',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        const userExists = !!user?.id;
        const technicianExists = !!entity?.technician?.id;
        if (!userExists || !technicianExists) {
          return false;
        }
        const userIsTechnician = user.id === entity.technician.id;
        return (
          userIsTechnician &&
          (entity.state == CommissioningStatusEnum.Incomplete ||
            entity.state == CommissioningStatusEnum.FeedbackNeeded ||
            entity.state == CommissioningStatusEnum.DefectReported) &&
          entity.area.name != 'GAS' &&
          entity.area.name != 'POWER'
        );
      },
      execute: ExecutionActionsTypes.saveAndActionPlusMail
    },
    {
      action: 'requestApprovalGas',
      actionName: 'Genehmigung anfragen',
      actionUrl: uri.cmCommissioningRequestRequestApproval,
      successMessage: 'Anfrage erfolgreich versandt',
      failureMessage: 'Fehler beim Versenden der Anfrage',
      subject: 'Ihre Anfrage zur Inbetriebnahme',
      validateForm: true,
      preActionHook: (
        actionInfo: ActionWithMoreInfoModel,
        { installationHasChanged }: CommissioningRequestDtoModel
      ) => {
        /**
         * Abhängig davon, ob der Wert "InstlalationHasChanged" gesetzt ist müssen wir verschiedene Aktionen ausführen
         * für den True fall wird der Schornsteinfeger in den Prozess einbezogen.
         * Um Abwärtskompatibel zu bleiben haben wir entschlossen die "Weiche" hier zu imlplementieren
         */
        actionInfo.actionUrl = installationHasChanged
          ? uri.cmCRGasRequestConfirmationChimneySweep
          : uri.cmCommissioningRequestRequestApproval;
      },
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          [
            CommissioningStatusEnum.Incomplete,
            CommissioningStatusEnum.DefectReported,
            CommissioningStatusEnum.FeedbackNeededByChimneySweep,
            CommissioningStatusEnum.FeedbackNeeded
          ].includes(entity.state) &&
          entity.area.name === 'GAS' &&
          user.id === entity.technician.id
        );
      },
      execute: ExecutionActionsTypes.saveAndActionPlusMail
    },
    {
      action: 'confirmAction',
      actionName: 'Antrag korrigieren',
      actionUrl: uri.cmCommissioningRegistrationCorrectBeforeApproval,
      successMessage: 'Anfrage erfolgreich nach unvollständig gesetzt',
      failureMessage: 'Fehler beim Versenden der Anfrage',
      subject: 'Ihre Anfrage zur Netzanmeldung',
      validateForm: false,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          (entity.state == CommissioningStatusEnum.Requested ||
            entity.state === CommissioningStatusEnum.Commissioned ||
            entity.state === CommissioningStatusEnum.NAVSent ||
            entity.state == CommissioningStatusEnum.ChimneySweepConfirmationRequested) &&
          this.hasRole(user, ['ROLE_INSTALLATEUR', 'ROLE_ADMIN_INSTALLATEUR'])
        );
      },
      execute: ExecutionActionsTypes.confirmAction
    },
    {
      action: 'requestApproval',
      actionName: 'Genehmigung anfragen',
      actionUrl: uri.cmCommissioningRequestRequestApproval,
      successMessage: 'Anfrage erfolgreich versandt',
      failureMessage: 'Fehler beim Versenden der Anfrage',
      subject: 'Ihre Anfrage zur Inbetriebnahme',
      customAction: CustomActionTypes.checkIfCRHasNonApproveGR,
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          (entity.state == CommissioningStatusEnum.Incomplete ||
            entity.state == CommissioningStatusEnum.DefectReported ||
            entity.state == CommissioningStatusEnum.FeedbackNeeded) &&
          entity.area.name == 'POWER' &&
          user.id == entity.technician.id
        );
      },
      execute: ExecutionActionsTypes.customAction
    },
    {
      action: 'createDocumentV2',
      actionName: 'Dokument erzeugen',
      actionUrl: '',
      successMessage: 'Dokument erfolgreich erzeugt',
      failureMessage: 'Fehler beim Erzeugen des Dokumentes',
      subject: '',
      validateForm: false,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return state !== CommonStatusEnum.Incomplete;
      },
      execute: ExecutionActionsTypes.createDocumentV2
    },
    {
      action: 'close',
      actionName: 'Abschluss (Monteur)',
      actionUrl: uri.cmCloseCommissioningRequest,
      successMessage: 'Inbetriebsetzung erfolgt',
      failureMessage: 'Fehler beim fertigmelden der Inbetriebnahme',
      customAction: CustomActionTypes.checkIfGRPlaceInServiceAndMaStrRegisterDates,
      subject: 'Inbetriebsetzung erfolgt',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          !this.dataShareService.tenantConfig.commissioning.showAdditionalDataOnCompletion &&
          this.authenticationService.isLoggedIn &&
          (entity.state == CommissioningStatusEnum.Approved ||
            entity.state == CommissioningStatusEnum.InstallationCompleted) &&
          this.hasRole(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.customAction
    },
    {
      action: 'closeV2',
      actionName: 'Abschluss (Monteur) / v2',
      tooltip: 'Neue Version des Abschlusses ',
      actionUrl: uri.cmCloseCommissioningRequest,
      successMessage: 'Inbetriebsetzung erfolgt',
      customForm: CustomFormTypes.CrCompletitionForm,
      execute: ExecutionActionsTypes.customFormWithCustomActionModal,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      failureMessage: 'Fehler beim fertigmelden der Inbetriebnahme',
      subject: 'Inbetriebsetzung erfolgt',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.dataShareService.tenantConfig.commissioning.showAdditionalDataOnCompletion &&
          this.authenticationService.isLoggedIn &&
          (entity.state == CommissioningStatusEnum.Approved ||
            entity.state == CommissioningStatusEnum.InstallationCompleted) &&
          this.hasRole(user, ['ROLE_EDITOR_BASE'])
        );
      }
    },
    {
      action: 'submitNewDate',
      actionName: 'Neuen Durchführungstermin festlegen',
      actionUrl: uri.cmCommissioningRequestChangeSupplyAppointment,
      successMessage: 'Nachricht erfolgreich verschickt.',
      failureMessage: 'Fehler beim Verschicken der Nachricht',
      customForm: CustomFormTypes.CrSubmitNewDateForm,
      tooltip:
        'Lässt den Sachbearbeiter einen neuen Durchführungstermin festlegen und versendet eine E-Mail an den Kunden und Installateur mit einer Benachrichtigung über den neuen Termin.',
      actionType: 'secondary',
      subject: 'Neuer Durchführungstermin',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.dataShareService.tenantConfig.commissioning.changeSupplyAppointmentActive &&
          this.authenticationService.isLoggedIn &&
          (entity.state == CommissioningStatusEnum.Approved ||
            entity.state == CommissioningStatusEnum.InstallationCompleted) &&
          this.hasRole(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail
    },
    {
      action: 'installationComplete',
      actionName: 'Fertigmeldung (Installateur)',
      actionUrl: uri.cmInstallationComplete,
      successMessage: 'Fertigmeldung erhalten',
      failureMessage: 'Fehler beim einreichen der Fertigmeldung',
      subject: 'Fertigmeldung erhalten',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.authenticationService.isLoggedIn &&
          entity.state === CommissioningStatusEnum.Approved &&
          ((this.dataShareService.tenantConfig.commissioning.gasConfig.activateTechnicianCompletionStep &&
            entity.area.name === 'GAS') ||
            (this.dataShareService.tenantConfig.commissioning.waterConfig.activateTechnicianCompletionStep &&
              entity.area.name === 'WATER'))
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'createERPOrder',
      actionName: 'SD-Beleg erzeugen',
      actionUrl: uri.cmCloseCommissioningRequest,
      successMessage: 'Beleg erzeugt',
      failureMessage: 'Fehler beim erzeugen des Beleges',
      customForm: CustomFormTypes.CreateERPOrder,
      actionType: 'secondary',
      subject: 'SD-Beleg erzeugt',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return (
          this.authenticationService.isLoggedIn &&
          this.hasRole(user, ['ROLE_EDITOR_BASE']) &&
          this.dataShareService.tenantConfig.commissioning.orderCreationActive
        );
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    },
    {
      action: 'checkIfErrors',
      actionName: 'Fehler prüfen',
      actionUrl: null,
      successMessage: '',
      failureMessage: '',
      subject: '',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return false;
      },
      execute: ExecutionActionsTypes.checkIfErrors
    },
    {
      action: 'ChimneySweepAccepted',
      actionName: 'Angaben bestätigen',
      actionUrl: uri.cmCRGasExternalChimneySweepConfirmed,
      successMessage: 'Bestätigung erfolgreich gesendet',
      failureMessage: 'Fehler beim Senden der Bestätigung',
      subject: '',
      validateForm: true,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return !this.authenticationService.isLoggedIn;
      },
      execute: ExecutionActionsTypes.executeAction
    },
    {
      action: 'ChimneySweepRejected',
      actionName: 'Ablehnung bestätigen',
      actionUrl: uri.cmCRGasExternalChimneySweepRejected,
      successMessage: 'Bestätigung erfolgreich gesendet',
      failureMessage: 'Fehler beim Senden der Bestätigung',
      subject: 'Ablehnung von Schornsteinfeger',
      commentRequested: 'Kommentar um die Aktion zu begründen',
      validateForm: false,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return !this.authenticationService.isLoggedIn;
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'changeStateCommissioningRequest',
      actionName: 'Status ändern',
      actionUrl: uri.cmCrChangeStatus,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      successMessage: 'Der Status wurde geändert',
      failureMessage: 'Fehler bei Statusänderung der Inbetriebnahme.',
      recipients: '',
      subject: 'Status ändern',
      validateForm: false,
      customForm: CustomFormTypes.CrChangeStatus,
      visibleCondition: (entity: any, state: string, user: UserDto): boolean => {
        return this.hasRole(user, ['ROLE_KEYUSER_COMMISSIONING']);
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    }
  ];

  constructor(
    private authenticationService: AuthenticationService,
    private dataShareService: DataShareService
  ) {}

  initSearchSortDto(
    hasBackOfficeSettings: boolean,
    formOrderRequest: FormGroup,
    INIcreatedDate: string,
    INIstateModifiedDate: string,
    INImodifiedDate: string,
    INIsupplyAppointment: string,
    INIsupplyAppointmentWishDate: string,
    searchSortDto: SearchSortDtoModel,
    columns: ColumnEditorModel[],
    listColumnsDefinition: any[]
  ): SearchSortDtoModel {
    searchSortDto = new SearchSortDtoModel();
    const searchDto = new SearchDtoModel();
    searchDto.resultType = ResultTypeEnum.OrderRequest;
    searchSortDto.searchDto = searchDto;
    const searchFilter: SearchDtoModel = new SearchDtoModel();
    const sortFilter: SortDtoModel = new SortDtoModel();
    sortFilter.fieldName = 'id';
    sortFilter.direction = CriteriaSortEnum.DESC;
    searchSortDto.searchDto = searchFilter;
    const sorts: Array<SortDtoModel> = new Array<SortDtoModel>();
    sorts.push(sortFilter);
    if (hasBackOfficeSettings) {
      searchSortDto = this.refreshFilters(
        formOrderRequest,
        INIcreatedDate,
        INIstateModifiedDate,
        INImodifiedDate,
        INIsupplyAppointment,
        INIsupplyAppointmentWishDate,
        searchSortDto,
        listColumnsDefinition
      );
      const idxColumn = columns.findIndex((x) => x.isSort);
      if (idxColumn > -1) {
        sorts[0].fieldName = columns[idxColumn].id;
        sorts[0].direction = columns[idxColumn].criteriaSort;
      }
    }
    searchSortDto.sorts = sorts;
    return searchSortDto;
  }

  refreshFilters(
    form: FormGroup,
    INIcreatedDate: string,
    INIstateModifiedDate: string,
    INImodifiedDate: string,
    INIsupplyAppointment: string,
    INIsupplyAppointmentWishDate: string,
    searchSortDto: SearchSortDtoModel,
    listColumnsDefinition: any[]
  ): SearchSortDtoModel {
    const listSearchCriteriaDto: Array<SearchCriteriaDtoModel> = new Array<SearchCriteriaDtoModel>();
    listColumnsDefinition.forEach((ele) => {
      if (ele.formField) {
        const value = form.controls[ele.id].value;
        if (value != '') {
          const searchCriteria: SearchCriteriaDtoModel = new SearchCriteriaDtoModel();
          searchCriteria.fieldName = ele.id;
          searchCriteria.operation = ele.searchCriteria;
          searchCriteria.fieldJunction = FieldJunctionEnum.AND;
          searchCriteria.fieldType = ele.fieldType;
          searchCriteria.nestedField = ele.nestedField;
          searchCriteria.nestedPath = ele.nestedPath;

          switch (ele.fieldType) {
            case 'relatedGRtoCR':
              if (value) {
                searchCriteria.filterValue = value.value;
              } else {
                searchCriteria.filterValue = '';
              }
              break;

            case 'MULTISELECT':
              searchCriteria.filterValue = 'multiselect';
              if (ele.id == 'area') {
                const values = [];
                value.forEach((e) => values.push(e.id));
                searchCriteria.multiFilterValues = values;
              }
              if (ele.id == 'companyLocation') {
                const values = [];
                value.forEach((e) => values.push(e.id));
                searchCriteria.multiFilterValues = values;
              }
              if (ele.id == 'state') {
                const states = [];
                value.forEach((e) => states.push(e.value));
                searchCriteria.multiFilterValues = states;
              }
              if (ele.id == 'responsibleParty') {
                const values = [];
                value.forEach((e) => values.push(e.value));
                searchCriteria.multiFilterValues = values;
              }
              break;
            case 'JOIN':
              if (ele.id == 'user') {
                /*
                 todo Michael GC set dropdown with of ng-select auto
                 if (value == null) {
                   searchCriteria.filterValue = ';
                 } else {
                   searchCriteria.filterValue = value.displayName;
                 }*/
                searchCriteria.filterValue = value;
              }
              if (ele.id == 'activities') {
                searchCriteria.filterValue = value;
              }
              break;
            default:
              searchCriteria.filterValue = value;
              break;
          }

          if (
            ele.id == 'createdDate' ||
            ele.id == 'stateModifiedDate' ||
            ele.id == 'modifiedDate' ||
            ele.id == 'supplyAppointment' ||
            ele.id == 'supplyAppointmentWishDate'
          ) {
            if (searchCriteria.filterValue) {
              searchCriteria.filterValue = this.dateToStringYYYYMMdd(searchCriteria.filterValue);
              switch (ele.id) {
                case 'createdDate': {
                  if (INIcreatedDate == null) {
                    INIcreatedDate = '';
                  } else {
                    INIcreatedDate = this.dateToStringYYYYMMdd(INIcreatedDate);
                  }
                  searchCriteria.filterValue = INIcreatedDate + searchCriteria.filterValue;
                  break;
                }
                case 'stateModifiedDate': {
                  if (INIstateModifiedDate == null) {
                    INIstateModifiedDate = '';
                  } else {
                    INIstateModifiedDate = this.dateToStringYYYYMMdd(INIstateModifiedDate);
                  }
                  searchCriteria.filterValue = INIstateModifiedDate + searchCriteria.filterValue;
                  break;
                }
                case 'modifiedDate': {
                  if (INImodifiedDate == null) {
                    INImodifiedDate = '';
                  } else {
                    INImodifiedDate = this.dateToStringYYYYMMdd(INImodifiedDate);
                  }
                  searchCriteria.filterValue = INImodifiedDate + searchCriteria.filterValue;
                  break;
                }
                case 'supplyAppointment': {
                  if (INIsupplyAppointment == null) {
                    INIsupplyAppointment = '';
                  } else {
                    INIsupplyAppointment = this.dateToStringYYYYMMdd(INIsupplyAppointment);
                  }
                  searchCriteria.filterValue = INIsupplyAppointment + searchCriteria.filterValue;
                  break;
                }
                case 'supplyAppointmentWishDate': {
                  if (INIsupplyAppointmentWishDate == null) {
                    INIsupplyAppointmentWishDate = '';
                  } else {
                    INIsupplyAppointmentWishDate = this.dateToStringYYYYMMdd(INIsupplyAppointmentWishDate);
                  }
                  searchCriteria.filterValue = INIsupplyAppointmentWishDate + searchCriteria.filterValue;
                  break;
                }
              }
            } else {
              searchCriteria.filterValue = '';
            }
          }
          listSearchCriteriaDto.push(searchCriteria);
        }
      }
    });
    searchSortDto.searchDto.filters = listSearchCriteriaDto;
    return searchSortDto;
  }

  dateToStringYYYYMMdd(currentDate: string): string {
    const date = new Date(currentDate);
    let dateToSearch: string = date.getFullYear().toString() + '-';
    const month = date.getMonth() + 1;
    if (month < 10) {
      dateToSearch = dateToSearch + '0';
    }
    dateToSearch = dateToSearch + month.toString() + '-';
    const day = date.getDate();
    if (day < 10) {
      dateToSearch = dateToSearch + '0';
    }
    dateToSearch = dateToSearch + day.toString();
    return dateToSearch;
  }

  hasRole(user: UserDto, roles: UserRole[]) {
    return user.roles.find((x) => roles.includes(x.name)) != undefined;
  }
}
